import config from '../../config';
import { Api, ApiConfig } from '../api';
import { StreamApi } from '../streamApi';

// 图片上传响应类型
export interface UploadImageResponse {
  success: boolean;
  image_id: string;
  image_url: string;
  message: string;
}

// Unified search parameters type
export interface ProductSearchParams {
  keyword?: string;      // Search keyword
  file?: File;          // Image file for search
  locale?: string;      // Language locale (e.g., 'en_US', 'zh_CN')
  country_code?: string; // Country code (e.g., 'US', 'GB')
  sort_type?: 'orders' | 'price';  // Sort by orders or price
  sort_order?: 'desc' | 'asc';     // Sort order: descending or ascending
  currency?: string;    // Currency code (e.g., 'USD')
  ship_to?: string;     // Shipping destination country
  page_size?: number;   // Number of results per page
  page_index?: number;  // Page number
  platform?: 'all' | 'aliexpress' | '1688' | 'alibaba';  // Platform channel
}

// 搜索响应类型
export interface SearchResponse {
  success: boolean;
  source: string;
  request_id: string;
  total_count: number;
  products: Product[];
  image_id: string | null;
  raw_response: Record<string, any>;
}

export interface ProductResponse {
  status: boolean;
  request_id: string;
  message: string;
  data: {
    productSet: ProductSet;
  }
}
// 商品类型
export interface Product {
  product_id: string;
  title: string;
  url: string;
  image_url: string;
  price: {
    current: string;
    original: string;
    currency: string;
    discount_percentage: string;
  };
  category: {
    primary_id: string;
    primary_name: string;
    secondary_id: string;
    secondary_name: string;
  };
  rating: string;
  sales_volume: string;
  shipping: {
    from_country: string;
  };
  similarity_score: number;
  platform: string;
}

// API 错误响应类型
export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

// 商品详情查询参数类型
export interface GetProductInfoParams {
  product_id: string;
  ship_to_country: string;
  target_currency: string;
  target_language: string;
  source_platform: string;
}

export interface ProductSet {
    title: string;
    descriptionHtml: string;
    productOptions: {
      name: string;
      position: number;
      values: {
        name: string;
      }[];
    }[];
    files: {
      originalSource: string;
      alt: string;
      filename: string;
      contentType: string;
    }[];
    variants: {
      optionValues: {
        optionName: string;
        name: string;
      }[];
      file: {
        originalSource: string;
        alt: string;
        filename: string;
        contentType: string;
      };
      price: number;
      cost?: number;
      sku: string;
    }[];
    metafields: any
  }

// 商品详情响应类型
export interface ProductInfo {
  productSet: ProductSet;
}

export interface SearchParams {
  query: string;
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
}

export interface SearchResponse {
  items: Array<{
    id: string;
    title: string;
    description: string;
    price: number;
    images: string[];
  }>;
  total: number;
  page: number;
  pageSize: number;
}

export interface ProductMatchParams {
  shop_id: string;
  order_item_id: string;
}

export interface BatchProductMatchParams {
  shop_id: string;
  order_item_ids: string[];
}

export interface SourceLogisticInformation {
  eta_max_day: number;
  eta_min_day: number;
  source_shipping_cost: number;
  shipping_fee_currency: string;
}

export interface SourceWholesaleInformation {
  min_quantity: string;
  wholesale_price: string;
}

export interface SourceStoreInformation {
  store_name: string;
  communication_rating: string;
  shipping_speed_rating: string;
}

export interface SourceItemInformation {
  sales_count: string;
  product_title: string;
  source_unit_cost: number;
  variant_image_url: string;
  source_product_cost: number;
  avg_evaluation_rating: string;
  currency_code: string;
}


export interface MatchedProduct {
  id: number;
  shop_id: string;
  order_id: string;
  order_item_id: string;
  variant_id: string;
  similarity_score: number;
  source_platform: string;
  source_url: string;
  source_product_summary: string;
  source_store_information: SourceStoreInformation;
  source_item_information: SourceItemInformation;
  source_wholesale_information: SourceWholesaleInformation;
  source_logistic_information: SourceLogisticInformation;
  created_at: string;
  updated_at: string;
}

export interface MatchedProductsResponse {
  results: {
    [key: string]: MatchedProduct[];
  };
}

export interface Response<T> {
  request_id?: string;
  status?: string;
  message?: string;
  data?: T;
}


export class SearchService {
    private api: Api;
    private streamApi: StreamApi;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
        this.streamApi = new StreamApi(config);
    }

    /**
   * 上传图片
   * @param file 要上传的图片文件
   * @returns 上传响应
   */
    public async uploadImage(file: File): Promise<UploadImageResponse> {
    // 验证文件
        const validation = this.validateFile(file);
        if (!validation.isValid) {
            throw new Error(validation.error);
        }

        const formData = new FormData();
        formData.append('file', file);

        return this.api.post<UploadImageResponse>('/api/salerio/search_image/upload', formData, {headers: {'Content-Type': 'multipart/form-data',},});
    }

    /**
   * 通过图片ID搜索商品
   * @param params 搜索参数
   * @returns 搜索结果
   */
    public async searchProducts(params: ProductSearchParams): Promise<SearchResponse> {
    // 验证参数
        if (!params.keyword && !params.file) {
            throw new Error('At least one of the following must be provided: an image or a product name.');
        }

        // 如果包含文件，验证文件
        if (params.file) {
            const validation = this.validateFile(params.file);
            if (!validation.isValid) {
                throw new Error(validation.error);
            }
        }

        const formData = new FormData();
        const {
            file, keyword, page_size, page_index, ...restParams 
        } = params;

        // 添加文件（如果存在）
        if (file) {
            formData.append('file', file);
        }

        // 添加其他参数
        Object.entries({
            keyword,
            page_size: page_size?.toString(),
            page_index: page_index?.toString(),
            ...restParams
        }).forEach(([key, value]) => {
            if (value !== undefined) {
                formData.append(key, value);
            }
        });

        return this.api.post<SearchResponse>('/api/salerio/product_search', formData, {headers: {'Content-Type': 'multipart/form-data',},});
    }

    /**
   * 验证文件是否合法
   * @param file 要验证的文件
   * @returns 验证结果
   */
    public validateFile(file: File): { isValid: boolean; error?: string } {
        if (file.size > config.constants.MAX_FILE_SIZE) {
            return {
                isValid: false,
                error: `The file size cannot exceed ${config.constants.MAX_FILE_SIZE / 1024 / 1024}MB`
            };
        }

        if (!config.constants.SUPPORTED_FILE_TYPES.includes(file.type)) {
            return {
                isValid: false,
                error: 'Unsupported file type'
            };
        }

        return { isValid: true };
    }

    public async GetMatchedProducts(params: ProductMatchParams){
        const { shop_id, order_item_id } = params;
        return this.api.get<Response<MatchedProductsResponse>>(
            `/api/shopify/order_product_match/${shop_id}/${order_item_id}`
        );
    }
    
    public async  BatchMatchedProducts (params:BatchProductMatchParams) {
        const { shop_id, order_item_ids } = params;
        return this.api.post<Response<MatchedProductsResponse>>(
            `/api/shopify/order_product_match_batch`,
            { order_item_ids, shop_id }
        );
    } 
    

    /**
   * 获取商品详情
   * @param params 商品详情查询参数
   * @returns 商品详情信息
   */
    public async getProductInfo(params: GetProductInfoParams): Promise<ProductResponse> {
        try {
            return this.api.get<ProductResponse>('/api/shopify/product_info', { params });
        } catch (error) {
            throw error;
        }
    }


}

export const searchService = new SearchService(
    {
        baseURL: config.services.search.apiHost,
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': config.services.search.apiKey
        }
    }
); 